import React from "react";
import {
  MessageSquare,
  AlertTriangle,
  BookOpen,
  ListChecks,
  Target,
  FileText,
  Tag,
  Users,
  Info,
  Calendar,
  Pill,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
  FileDown,

} from "lucide-react";
import { codeArticles } from "../data/codeArticles";
import { supabase } from "../supabaseClient";

// Utility function to handle article content extraction
const getArticleContent = (articleData, reference) => {
  if (!articleData || !reference) return null;

  // Extract the base article number and subsection
  const [baseArticle, ...rest] = reference.split(" ");
  const hasSubsection = rest.join(" ").toLowerCase().includes("onder");

  if (!hasSubsection) {
    return {
      header: articleData.header,
      content: articleData.text,
    };
  }

  // Extract subsection letter
  const subsectionLetter = rest
    .join(" ")
    .toLowerCase()
    .split("onder")[1]
    .trim();

  if (articleData.subsections && articleData.subsections[subsectionLetter]) {
    return {
      header: `${articleData.header} - ${subsectionLetter}`,
      introduction: articleData.introduction,
      content: articleData.subsections[subsectionLetter],
    };
  }

  // Fallback to full text if subsection not found
  return {
    header: articleData.header,
    content: articleData.text,
  };
};

const AdviceAnalysis = ({ data }) => {

  const [selectedImage, setSelectedImage] = React.useState(null);
  const [images, setImages] = React.useState([]);


  // In your ComplaintAnalysis component, add this effect:
  React.useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  // Add this function to handle image navigation
  const navigateImage = (direction) => {
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    if (direction === 'next' && currentIndex < images.length - 1) {
      setSelectedImage(images[currentIndex + 1]);
    } else if (direction === 'prev' && currentIndex > 0) {
      setSelectedImage(images[currentIndex - 1]);
    }
  };


  React.useEffect(() => {
    const fetchImages = async () => {
      // First log the full data to see its structure
      console.log('Full data:', data);

      // Try to get zaak_id from multiple possible locations
      const zaakId = data.basis_informatie?.zaak_id ||
        data.id ||
        window.location.pathname.split('/').pop();

      console.log('Using zaakId:', zaakId);

      if (!zaakId) {
        console.log('No zaak_id found');
        return;
      }

      const { data: zaakImages, error } = await supabase
        .from('ZaakImages')
        .select('*')
        .eq('zaak_id', zaakId);

      if (error) {
        console.error('Supabase error:', error);
        return;
      }

      console.log('Found images:', zaakImages);
      setImages(zaakImages || []);
    };

    fetchImages();
  }, [data]);

  if (!data) return null;

  const {
    basis_informatie,
    inhoud,
    beoordeling,
    artikelen,
    praktijk,
    metadata,
    onderwerp,
    tijdlijn,
    praktische_relevantie,
    relevante_artikelen,
  } = data;

  const formatDate = (date) => {
    // Add date formatting logic here if needed
    return date;
  };

  return (
    <div className="space-y-6">
      {/* First Row: Question/Answer and Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Question and Answer */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-nn-500" />
            De adviesvraag en antwoord
          </h2>
          <div className="space-y-4">
            {onderwerp?.vraag && (
              <div>
                <h3 className="text-sm font-medium text-nn-gray-500 mb-2">
                  Vraag
                </h3>
                <p className="text-nn-gray-700">{onderwerp.vraag}</p>
              </div>
            )}
            {beoordeling?.kernpunt && (
              <div>
                <h3 className="text-sm font-medium text-nn-gray-500 mb-2">
                  Kernpunt
                </h3>
                <p className="text-nn-gray-700 whitespace-pre-wrap">
                  {beoordeling.kernpunt}
                </p>
              </div>
            )}
            {beoordeling?.conclusie && (
              <div>
                <h3 className="text-sm font-medium text-nn-gray-500 mb-2">
                  Conclusie
                </h3>
                <p className="text-nn-gray-700 whitespace-pre-wrap">
                  {beoordeling.conclusie}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Reclame-uiting */}
        {onderwerp?.reclameuiting && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-nn-500" />
              Reclame-uiting
            </h2>
            <div className="prose max-w-none">
              <div className="text-nn-gray-700">{onderwerp.reclameuiting}</div>
            </div>
          </div>
        )}

        {/* Tijdlijn */}
        {tijdlijn?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-nn-500 text-lg font-semibold mb-6 flex items-center gap-2">
              <Clock className="h-5 w-5 text-nn-500" />
              Tijdlijn
            </h2>
            <div className="relative pl-8">
              <div
                className="absolute left-[11px] w-0.5 bg-nn-gray-200"
                style={{ top: "6px", bottom: "6px" }}
              />
              <div className="space-y-8">
                {tijdlijn.map((event, idx) => (
                  <div key={idx} className="relative flex items-start">
                    <div
                      className="absolute -left-[24.5px] w-3 h-3 rounded-full bg-white border-2 border-nn-500"
                      style={{ top: "50%", transform: "translateY(-50%)" }}
                    />
                    <div className="flex-1 bg-nn-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-nn-500 mb-1">
                        {formatDate(event.datum)}
                      </div>
                      <div className="text-nn-gray-700">{event.feit}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Categories */}
      {basis_informatie?.hoofdcategorie?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
            <Tag className="h-5 w-5 text-nn-500" />
            Onderwerp
          </h2>
          <div className="flex flex-wrap gap-2">
            {basis_informatie.hoofdcategorie.map((categorie, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-nn-gray-100 text-nn-gray-700 rounded-full text-sm"
              >
                {categorie}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Practical Lessons */}
      {praktische_relevantie?.best_practices?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
            <Info className="h-5 w-5 text-nn-500" />
            Praktische lessen
          </h2>
          <ul className="list-disc list-inside space-y-2 text-nn-gray-700">
            {praktische_relevantie.best_practices.map((les, idx) => (
              <li key={idx}>{les}</li>
            ))}
          </ul>
        </div>
      )}



      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 p-4 flex flex-col items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] relative flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Description if exists */}
            {selectedImage.description && (
              <div className="mb-4">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selectedImage.description}
                </p>
              </div>
            )}

            {/* Image container */}
            <div className="relative flex-1 flex items-center justify-center min-h-0">
              {/* Previous button */}
              {images.findIndex(img => img.id === selectedImage.id) > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('prev');
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
                >
                  <ChevronLeft className="h-6 w-6 text-gray-700" />
                </button>
              )}

              <img
                src={selectedImage.image_url}
                alt={`Afbeelding ${selectedImage.id}`}
                className="w-auto max-h-[calc(80vh-10rem)] object-contain"
              />

              {/* Next button */}
              {images.findIndex(img => img.id === selectedImage.id) < images.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('next');
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
                >
                  <ChevronRight className="h-6 w-6 text-gray-700" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Third Row: Relevante artikelen */}
      {relevante_artikelen?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-nn-500 text-lg font-semibold flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-nn-500" />
              Relevante artikelen uit de Code
            </h2>

            <a
              href="https://mwpkmlumjrauwywhfgfo.supabase.co/storage/v1/object/public/pdf-uploads/Bundel%20Gedragscode%20Jan%201%202024%20(1).pdf?t=2025-01-09T09%3A28%3A38.825Z"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-nn-500 hover:text-nn-600 transition-colors duration-150"
            >
              <FileDown className="h-4 w-4" />
              <span>Download Gedragscode</span>
            </a>
          </div>
          <div className="space-y-6">
            {relevante_artikelen.map((artikel, idx) => {
              const baseArticleNum = artikel.artikel.split(" ")[0];
              const articleContent = getArticleContent(
                codeArticles[baseArticleNum],
                artikel.artikel
              );

              if (!articleContent) return null;

              return (
                <div key={idx} className="bg-nn-gray-50 rounded-lg p-4">
                  {/* Article Header */}
                  <h3 className="font-medium text-nn-gray-900 mb-3">
                    Artikel {baseArticleNum} -{" "}
                    {codeArticles[baseArticleNum]?.header}
                    {artikel.artikel.toLowerCase().includes("onder") &&
                      ` (${artikel.artikel.split("onder")[1].trim()})`}
                  </h3>

                  {/* Introduction (only for subsections) */}
                  {articleContent.introduction && (
                    <p className="text-nn-gray-700 mb-2 italic">
                      {articleContent.introduction}
                    </p>
                  )}

                  {/* Article Content */}
                  <p className="text-nn-gray-700 mb-4">
                    {articleContent.content}
                  </p>

                  {/* Relevance Explanation */}
                  {artikel.reden_relevant && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-nn-gray-700">
                        Waarom relevant:
                      </h4>
                      <p className="text-sm text-nn-gray-500">
                        {artikel.reden_relevant}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdviceAnalysis;
